// TopNav — persistent nav per frontend-design §3 TopNav.
// Role-aware nav links; user menu with sign-out; live region for the
// role label so role-change probes (F-001) can be announced.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { NavLink } from 'react-router-dom';
import { usePrincipalContext } from '@/auth/PrincipalContext';
import { navLinks } from '@/auth/permissions';
import { formatRole } from '@/utils/format';
import type { Role } from '@/types/domain';

export function TopNav() {
  const { principal, signOut, bootstrapping } = usePrincipalContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // F-001 role-change announcement. The Principal context replaces
  // atomically when a refresh probe observes a different role. We
  // detect that change here and rewrite the live-region with the
  // design's wording ("Your role is now <X>"). On first mount the
  // live region carries the steady-state wording.
  const previousRoleRef = useRef<Role | null>(null);
  const [roleAnnouncement, setRoleAnnouncement] = useState<string>('');
  useEffect(() => {
    if (!principal) {
      previousRoleRef.current = null;
      setRoleAnnouncement('');
      return;
    }
    const prev = previousRoleRef.current;
    if (prev === null) {
      // Initial mount — describe the steady state, not a change.
      setRoleAnnouncement(`Your role is ${formatRole(principal.role)}.`);
    } else if (prev !== principal.role) {
      setRoleAnnouncement(`Your role is now ${formatRole(principal.role)}.`);
    }
    previousRoleRef.current = principal.role;
  }, [principal]);

  // Close on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: globalThis.MouseEvent) => {
      const target = e.target as Node | null;
      if (
        target &&
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const handleMenuKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setMenuOpen(false);
      triggerRef.current?.focus();
    }
  }, []);

  const onSignOut = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  }, [signOut, signingOut]);

  if (bootstrapping || !principal) {
    return (
      <header className="topnav topnav-loading">
        <div className="topnav-inner">
          <span className="topnav-logo" aria-label="SOW Manager">
            SOW Manager
          </span>
        </div>
      </header>
    );
  }

  const links = navLinks(principal);
  const initials = (principal.email[0] ?? '?').toUpperCase();

  return (
    <header className="topnav">
      <div className="topnav-inner">
        <NavLink to="/" className="topnav-logo">
          SOW Manager
        </NavLink>
        <nav aria-label="Primary">
          <ul className="topnav-links" role="list">
            {links.map((link) => (
              <li key={link.to}>
                <NavLink to={link.to} end={link.to === '/'}>
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="topnav-user">
          {/* Polite live region for role announcements (F-001). */}
          <span
            aria-live="polite"
            className="visually-hidden"
            data-testid="role-live-region"
          >
            {roleAnnouncement}
          </span>
          <button
            type="button"
            ref={triggerRef}
            className="topnav-user-trigger"
            aria-haspopup="menu"
            aria-expanded={menuOpen ? 'true' : 'false'}
            aria-label={`Account menu for ${principal.email}`}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="topnav-avatar" aria-hidden="true">
              {initials}
            </span>
          </button>
          {menuOpen ? (
            <div
              ref={menuRef}
              role="menu"
              className="topnav-user-menu"
              onKeyDown={handleMenuKeyDown}
            >
              <div className="user-menu-header">
                <div className="user-email">{principal.email}</div>
                <div className="user-role">{formatRole(principal.role)}</div>
              </div>
              <button
                type="button"
                role="menuitem"
                className="user-menu-item"
                onClick={onSignOut}
                disabled={signingOut}
                aria-busy={signingOut ? 'true' : 'false'}
              >
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
